import { useState, useEffect, useRef } from 'react';
import { SLIDES } from './WalkthroughSlides';
import { markWalkthroughSeen } from '../lib/api';
import { getUser, setUser } from '../lib/auth';

const ORANGE = '#F37428';

export default function WalkthroughModal({ onClose }) {
  const [idx, setIdx]         = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const slide    = SLIDES[idx];
  const isLast   = idx === SLIDES.length - 1;
  const isFirst  = idx === 0;

  useEffect(() => {
    clearInterval(timerRef.current);
    setProgress(0);
    if (!playing) return;
    const duration = 7000;
    const start = Date.now();
    timerRef.current = setInterval(() => {
      const pct = Math.min((Date.now() - start) / duration, 1);
      setProgress(pct);
      if (pct >= 1) {
        clearInterval(timerRef.current);
        if (!isLast) setIdx(i => i + 1);
        else setPlaying(false);
      }
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [idx, playing]);

  function go(newIdx) {
    clearInterval(timerRef.current);
    setProgress(0);
    setIdx(newIdx);
  }

  function togglePlay() {
    if (playing) { clearInterval(timerRef.current); setPlaying(false); }
    else setPlaying(true);
  }

  function dismiss(dontShow) {
    clearInterval(timerRef.current);
    if (dontShow) {
      markWalkthroughSeen().catch(() => {});
      const u = getUser();
      if (u) setUser({ ...u, walkthrough_seen_at: new Date().toISOString() });
    }
    onClose();
  }

  const MockComponent = slide.Mock;

  return (
    /* Backdrop */
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(10,37,64,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
      backdropFilter: 'blur(3px)',
    }}
      onClick={e => { if (e.target === e.currentTarget) dismiss(false); }}
    >
      {/* Card */}
      <div style={{
        background: '#fff',
        borderRadius: 18,
        width: '100%',
        maxWidth: 900,
        boxShadow: '0 32px 100px rgba(0,0,0,0.35)',
        overflow: 'hidden',
        fontFamily: 'Inter, system-ui, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Card header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 900, fontSize: 15, color: '#0f172a' }}>
              MASTERFILE<span style={{ color: ORANGE }}>AI</span>
            </span>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>— How It Works</span>
          </div>
          <button onClick={() => dismiss(false)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', fontSize: 20, lineHeight: 1, padding: '2px 6px',
          }}>×</button>
        </div>

        {/* Slide frame */}
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', background: '#f8fafc' }}>
          <MockComponent />
          {/* Step badge — bottom right, away from logo */}
          <div style={{
            position: 'absolute', bottom: 14, right: 14,
            background: 'rgba(10,37,64,0.65)', backdropFilter: 'blur(4px)',
            borderRadius: 20, padding: '4px 12px',
            fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.9)',
          }}>
            {idx + 1} / {SLIDES.length}
          </div>
          {/* Progress bar */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(0,0,0,0.08)' }}>
            <div style={{ height: '100%', width: `${progress * 100}%`, background: ORANGE, transition: 'width 0.08s linear' }} />
          </div>
        </div>

        {/* Slide description */}
        <div style={{ padding: '18px 24px 0', minHeight: 72 }}>
          {slide.title && (
            <div style={{ fontWeight: 800, fontSize: 20, color: '#0f172a', marginBottom: 7 }}>
              {slide.title}
            </div>
          )}
          {slide.body && (
            <div style={{ fontSize: 15, color: '#475569', lineHeight: 1.7 }}>
              {slide.body}
            </div>
          )}
        </div>

        {/* Controls + dot nav */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px 20px',
        }}>
          {/* Dot nav */}
          <div style={{ display: 'flex', gap: 7 }}>
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => go(i)} style={{
                width: i === idx ? 22 : 8, height: 8, borderRadius: 4,
                background: i === idx ? ORANGE : '#e2e8f0',
                border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.2s',
              }} />
            ))}
          </div>

          {/* Playback + nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <NavBtn onClick={() => go(idx - 1)} disabled={isFirst}>‹ Back</NavBtn>
            <button onClick={togglePlay} style={{
              background: playing ? 'transparent' : ORANGE,
              border: `2px solid ${ORANGE}`,
              color: playing ? ORANGE : '#fff',
              borderRadius: 50, width: 42, height: 42,
              fontSize: 16, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}>
              {playing ? '⏸' : '▶'}
            </button>
            {isLast ? (
              <button onClick={() => dismiss(false)} style={{
                background: ORANGE, color: '#fff', border: 'none',
                borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}>Done</button>
            ) : (
              <NavBtn onClick={() => go(idx + 1)} disabled={false}>Next ›</NavBtn>
            )}
          </div>

          {/* Don't show again */}
          <button onClick={() => dismiss(true)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', fontSize: 12, textDecoration: 'underline',
          }}>
            Don't show again
          </button>
        </div>
      </div>
    </div>
  );
}

function NavBtn({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: 'none', border: '1px solid #e2e8f0',
      color: disabled ? '#e2e8f0' : '#475569',
      borderRadius: 7, padding: '8px 18px', fontSize: 13,
      cursor: disabled ? 'default' : 'pointer', fontWeight: 600,
    }}>{children}</button>
  );
}
